'use client';

import type { Variants } from 'framer-motion';
import { AnimatePresence, m as motion } from 'framer-motion';
import { useTheme } from 'next-themes';
import styled from 'styled-components';

import { CheckOutlined } from '@zoomit/icons';
import { ClickAwayListener, Flex } from '@zoomit/primitives';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { themeStates } from '../../constants/theme';
import { onThemeChange } from '../../helpers/events';

type Props = {
  open: boolean;
  setOpen(open: boolean): void;
};

const menuVariants: Variants = {
  closed: {
    opacity: 0,
    visibility: 'hidden',
  },
  open: {
    opacity: 1,
    visibility: 'visible',
  },
};

const ToggleTheme = (props: Props) => {
  const { open, setOpen } = props;
  const { theme: selectedTheme, setTheme } = useTheme();

  return (
    <ClickAwayListener clickAwayEnable onClickAway={() => setOpen(false)}>
      <AnimatePresence>
        {open && (
          <DropDown
            data-testid="theme-toggle-dropdown"
            animate="open"
            initial="open"
            exit="closed"
            variants={menuVariants}
            transition={{ duration: 0.2 }}
          >
            {Object.entries(themeStates).map(([key, value]) => {
              const Icon = value.icon;
              const type = key as 'light' | 'dark' | 'system';
              const isActive = selectedTheme === type;
              return (
                <DropDownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    setTheme(type);
                    onThemeChange(type === 'system' ? 'auto' : type);
                  }}
                  $isActive={isActive}
                  key={key}
                  spacing={{ py: 'x2', px: 'x6' }}
                  gap={0.8}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Flex gap={0.8} alignItems="center">
                    <Icon
                      fill={cssVar(isActive ? theme.text : theme.grey1)}
                      stroke={cssVar(isActive ? theme.text : theme.grey1)}
                    />
                    <Typography textColor={cssVar(isActive ? theme.text : theme.grey1)}>
                      {value.label}
                    </Typography>
                  </Flex>
                  {isActive && <CheckOutlined fill={cssVar(theme.primary2)} />}
                </DropDownItem>
              );
            })}
          </DropDown>
        )}
      </AnimatePresence>
    </ClickAwayListener>
  );
};

export default ToggleTheme;

const DropDown = styled(motion.div)`
  background-color: var(${theme.card});
  position: absolute;
  left: 0;
  top: calc(100% + 1rem);
  box-shadow: 0px 4px 10px 0px rgba(50, 50, 60, 0.2);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: 15.8rem;
  min-width: 22rem;
  padding: 1.6rem 0;
`;

const DropDownItem = styled(Flex)<{ $isActive: boolean }>`
  height: 42px;
  cursor: pointer;
`;
